const MAX_RETRIES = 2;

/**
 * Loads an image from a URL, validating that naturalWidth > 0 after load.
 * Retries up to MAX_RETRIES times on failure or zero-dimension result before rejecting.
 *
 * @param src - The image URL to load
 * @param crossOrigin - The crossOrigin attribute to set on the image element (default: 'anonymous')
 * @returns A Promise that resolves with the loaded HTMLImageElement
 */
export const loadImageWithRetry = (src: string, crossOrigin = 'anonymous'): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const attempt = () => {
      const img = new window.Image();
      img.crossOrigin = crossOrigin;

      img.onload = () => {
        if (img.naturalWidth > 0) {
          resolve(img);
        } else if (attempts < MAX_RETRIES) {
          attempts++;
          attempt();
        } else {
          reject(new Error('Image loaded with zero dimensions after retries.'));
        }
      };

      img.onerror = () => {
        if (attempts < MAX_RETRIES) {
          attempts++;
          attempt();
        } else {
          reject(new Error('Image failed to load after retries.'));
        }
      };

      img.src = src;
    };

    attempt();
  });
};
